/**
 * Netxol Admin API client — /api/platform with the platform session (never the ISP console's session).
 * The access token is kept in memory only; the refresh token is an HttpOnly cookie Core sets for /api/platform/auth
 * (requests say X-Netxol-Client: web). On admin.one.netxol.com it is also a different origin from the console.
 */
import { useEffect, useState } from 'react';
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiError } from '@/shared/api/client';
import type { Fetcher, Paged } from '@/shared/components/data/DataTable';

export interface Staff { id: string; email: string; name: string; role: string; roleKey: string; caps: string[] }
interface Session {
  accessToken: string | null; expiresAt: number | null; user: Staff | null; ready: boolean;
  set: (s: { accessToken: string; expiresAt: number; user: Staff }) => void; clear: () => void;
}
axios.defaults.headers.common['X-Netxol-Client'] = 'web';
export const useAdmin = create<Session>()(persist((set) => ({
  accessToken: null, expiresAt: null, user: null, ready: false,
  set: (s) => set({ accessToken: s.accessToken, expiresAt: s.expiresAt, user: s.user, ready: true }),
  clear: () => set({ accessToken: null, expiresAt: null, user: null, ready: true }),
}), { name: 'netxol.admin.session', partialize: (s) => ({ user: s.user }) as Partial<Session> }));   // never a token

/** After the page loads: a new access token from the refresh cookie, if there is a session. */
export async function restoreAdminSession() {
  try { const { data } = await axios.post('/api/platform/auth/refresh', {}); useAdmin.getState().set(data); } catch { useAdmin.getState().clear(); }
}

export const can = (u: Staff | null, ...caps: string[]) => !!u && (u.caps.includes('super') || caps.some((c) => u.caps.includes(c)));
export const useCan = (...caps: string[]) => can(useAdmin((s) => s.user), ...caps);

export const plat = axios.create({ baseURL: '/api/platform', timeout: 60_000 });
let refreshing: Promise<string | null> | null = null;
async function refresh() {
  const { set, clear } = useAdmin.getState();
  try { const { data } = await axios.post('/api/platform/auth/refresh', {}); set(data); return data.accessToken as string; } catch { clear(); return null; }
}
plat.interceptors.request.use(async (cfg) => {
  let { accessToken, expiresAt } = useAdmin.getState();
  if (accessToken && expiresAt && expiresAt * 1000 - Date.now() < 60_000) { refreshing ??= refresh().finally(() => { refreshing = null; }); accessToken = await refreshing; }
  if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
  cfg.headers['Accept-Language'] = localStorage.getItem('nx.lang') ?? 'en';
  return cfg;
});
plat.interceptors.response.use(undefined, async (err: AxiosError) => {
  const cfg = err.config as AxiosRequestConfig & { _retried?: boolean };
  if (err.response?.status === 401 && cfg && !cfg._retried && !String(cfg.url).includes('/auth/')) {
    cfg._retried = true; refreshing ??= refresh().finally(() => { refreshing = null; });
    const tok = await refreshing;
    if (tok) { cfg.headers = { ...cfg.headers, Authorization: `Bearer ${tok}` }; return plat(cfg); }
  }
  throw err;
});

const clean = (p?: Record<string, unknown>) => (p ? Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== '' && v !== null)) : undefined);
export function useP<T = any>(path: string | null, params?: Record<string, unknown>, opts?: Partial<UseQueryOptions<T>>) {
  return useQuery<T>({ queryKey: ['plat', path, params], queryFn: async () => (await plat.get<T>(path!, { params: clean(params) })).data, enabled: !!path, placeholderData: keepPreviousData, ...opts });
}
/** POST / PATCH / PUT / DELETE with a toast and a refresh of the admin data. */
export function usePAction<TBody = any, TOut = any>(method: 'post' | 'patch' | 'put' | 'delete', path: string | ((b: TBody) => string),
  opts: { success?: string | ((o: TOut) => string); onSuccess?: (o: TOut) => void } = {}) {
  const qc = useQueryClient();
  return useMutation<TOut, unknown, TBody>({
    mutationFn: async (body) => {
      const url = typeof path === 'function' ? path(body) : path;
      return (method === 'delete' ? await plat.delete<TOut>(url, { data: body }) : await plat[method]<TOut>(url, body)).data;
    },
    onSuccess: (o) => { if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(o) : opts.success); void qc.invalidateQueries({ queryKey: ['plat'] }); opts.onSuccess?.(o); },
    onError: (e) => toast.error(apiError(e)),
  });
}
/** DataTable reading a paged admin list. */
export const pagedFetcher = <T,>(path: string): Fetcher<T> => async (q) => (await plat.get<Paged<T>>(path, { params: clean(q) })).data;

/** An image from the platform API (needs the staff token, so a plain <img src> cannot load it): an object URL. */
export function usePlatformImage(path: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!path) { setUrl(null); return; }
    let u: string | null = null; let gone = false;
    plat.get(path, { responseType: 'blob' }).then((r) => { if (!gone) { u = URL.createObjectURL(r.data); setUrl(u); } }).catch(() => setUrl(null));
    return () => { gone = true; if (u) URL.revokeObjectURL(u); };
  }, [path]);
  return url;
}
